/** `sections/hexagon-callout.liquid` — `<hexagon-callout>`: drives a
 *  scroll-based parallax on the decorative `.hexagon-callout-hex` tiles. Each
 *  tile gets its own speed so they drift at slightly different rates as the
 *  section scrolls through the viewport. The offset is measured from the point
 *  where the section center meets the viewport center, so tiles sit at their
 *  design position when the section is centered.
 */

// Per-tile parallax speed (px of translate per px of section-center offset).
// Mixed signs so neighbouring tiles drift in opposite directions.
const HEX_SPEEDS = {
  'hexagon-callout-hex-top-center': 0.08,
  'hexagon-callout-hex-top-right': 0.06,
  'hexagon-callout-hex-left': -0.1,
  'hexagon-callout-hex-bottom-right': 0.07,
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

class HexagonCallout extends HTMLElement {
  #hexes = []
  #ticking = false
  #reducedMotion

  connectedCallback() {
    this.#reducedMotion = window.matchMedia?.(REDUCED_MOTION_QUERY)
    this.#setupParallax()
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll)
    this.#reducedMotion?.removeEventListener?.('change', this.#onReducedMotionChange)
  }

  #setupParallax() {
    this.#hexes = Array.from(this.querySelectorAll('.hexagon-callout-hex')).map((el) => {
      const speedClass = Object.keys(HEX_SPEEDS).find((cls) => el.classList.contains(cls))
      return { el, speed: HEX_SPEEDS[speedClass] ?? 0 }
    })

    if (this.#hexes.length === 0) return

    this.#reducedMotion?.addEventListener?.('change', this.#onReducedMotionChange)

    if (this.#reducedMotion?.matches) return

    window.addEventListener('scroll', this.#onScroll, { passive: true })
    this.#applyParallax()
  }

  #onReducedMotionChange = () => {
    if (this.#reducedMotion?.matches) {
      window.removeEventListener('scroll', this.#onScroll)
      this.#resetParallax()
    } else {
      window.addEventListener('scroll', this.#onScroll, { passive: true })
      this.#applyParallax()
    }
  }

  #onScroll = () => {
    if (this.#ticking) return
    this.#ticking = true
    window.requestAnimationFrame(() => {
      this.#applyParallax()
      this.#ticking = false
    })
  }

  #applyParallax() {
    const viewportHeight = window.innerHeight
    const rect = this.getBoundingClientRect()

    // Skip work when the section is well outside the viewport.
    if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) return

    // Offset of the section center from the viewport center; 0 when centered.
    const offset = rect.top + rect.height / 2 - viewportHeight / 2

    for (const { el, speed } of this.#hexes) {
      const translate = (offset * speed).toFixed(2)
      el.style.transform = `translate3d(0, ${translate}px, 0)`
    }
  }

  #resetParallax() {
    for (const { el } of this.#hexes) {
      el.style.transform = ''
    }
  }
}

if (!customElements.get('hexagon-callout')) {
  customElements.define('hexagon-callout', HexagonCallout)
}
